import { Spinner } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationPipeline, ExpertReportPipelineScope } from '@/entities/search';
import { ConfirmationButton } from '@/shared/ui';
import { PipelineActions } from './PipelineActions';

type Props = {
  /** Запуск обработки есть в блоке: право на массовый запуск */
  canRunPipeline: boolean;
  /** Удаление есть в блоке: право на удаление диалогов */
  canDeleteConversations: boolean;
  /** Число отмеченных диалогов — выводится в блоке «Выбрано: N» и называется в подтверждении удаления */
  selectedCount: number;
  /** Есть над чем выполнять действие: отмечена хотя бы одна строка */
  hasSelection: boolean;
  /** Действие над отмеченными (или подготовка их состава) уже идёт — блокирует новые действия */
  isPending: boolean;
  /** Идёт удаление — подтверждение показывает ожидание */
  isDeletePending: boolean;
  /** Идёт сбор состава «выбрать всё» — вместо числа показывается индикатор ожидания */
  isPreparingSelection: boolean;
  /** Запуск выбранной обработки, у генерации заключений — с выбранной областью */
  onRun: (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => void;
  /** Удаление отмеченных диалогов после подтверждения */
  onDelete: () => Promise<void>;
};

/**
 * Блок массовых действий, раскрываемый кнопкой «Действия» под строкой поиска (по аналогии с
 * панелью поиска по содержимому диалога): действия над отмеченными диалогами — каждое под своим
 * разрешением — и счётчик отмеченных. Сам выбор ведётся чекбоксами в строках и мастер-чекбоксом
 * в шапке таблицы.
 */
export const MassActionsBar: FC<Props> = ({
  canRunPipeline,
  canDeleteConversations,
  selectedCount,
  hasSelection,
  isPending,
  isDeletePending,
  isPreparingSelection,
  onRun,
  onDelete,
}) => {
  const { t } = useTranslation(['dialogs']);

  return (
    <div className="flex flex-row flex-wrap items-center gap-3">
      {canRunPipeline && <PipelineActions hasSelection={hasSelection} isPending={isPending} onRun={onRun} />}
      {canDeleteConversations && (
        <ConfirmationButton
          onConfirm={onDelete}
          isLoadingConfirm={isDeletePending}
          title={t('dialogs:dialogListSection.deleteDialogs.confirmTitle')}
          description={t('dialogs:dialogListSection.deleteDialogs.confirmDescription', { count: selectedCount })}
          trigger={{
            variant: 'secondary',
            size: 'sm',
            disabled: !hasSelection || isPending,
            children: t('dialogs:dialogListSection.deleteDialogs.label'),
          }}
        />
      )}
      {isPreparingSelection ? (
        // «Выбрать всё» ещё собирает состав выборки — вместо промежуточного числа показываем ожидание.
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground">
          {t('dialogs:dialogListSection.massActions.selectedPreparing')}
          <Spinner className="size-4" />
        </span>
      ) : (
        <span className="text-sm whitespace-nowrap text-muted-foreground">
          {t('dialogs:dialogListSection.massActions.selectedCount', { count: selectedCount })}
        </span>
      )}
    </div>
  );
};
